/* ═══════════════════════════════════════════════════════════
   ASTROITE — Language selector

   Accessible popover listbox (not a blind cycle): every language
   is visible, the active one is checked, and it works by tap,
   mouse, and keyboard. Tap targets are ≥44px (the mobile bug was
   a ~24px hit area). Auto-scales to any number of LANGS.

   <LangToggle />              — light variant (cream surfaces)
   <LangToggle variant="dark"/>— dark variant (maroon surfaces)
   ═══════════════════════════════════════════════════════════ */

import { useRef, useState, useEffect, useCallback } from 'react';
import { LANGS, LANG_LABELS, LANG_NAMES, useLang } from '../i18n';

function IconGlobe({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

function IconCaret({ open }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ transition: 'transform 0.18s', transform: open ? 'rotate(180deg)' : 'none', opacity: 0.7 }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function LangToggle({ variant = 'light' }) {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside tap / Escape.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        if (btnRef.current) btnRef.current.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = useCallback((code) => {
    setLang(code);
    setOpen(false);
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8);
    if (btnRef.current) btnRef.current.focus();
  }, [setLang]);

  const dark = variant === 'dark';

  const btnStyle = dark
    ? { background: 'rgba(30,0,0,0.5)', border: '1px solid rgba(201,130,26,0.35)', color: 'rgba(255,248,231,0.85)', backdropFilter: 'blur(8px)' }
    : { background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(212,160,23,0.35)', color: 'var(--text-secondary, #6B3A0A)', boxShadow: '0 1px 4px rgba(180,100,0,0.07)' };

  const menuStyle = dark
    ? { background: 'rgba(40,4,8,0.98)', border: '1px solid rgba(201,130,26,0.35)', boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }
    : { background: '#FFFDF8', border: '1px solid rgba(212,160,23,0.30)', boxShadow: '0 10px 30px rgba(120,60,0,0.18)' };

  function rowStyle(active) {
    if (dark) {
      return {
        color: active ? '#F5D76E' : 'rgba(255,248,231,0.85)',
        background: active ? 'rgba(212,175,55,0.14)' : 'transparent',
      };
    }
    return {
      color: active ? 'var(--saffron-dark, #BF360C)' : 'var(--text-dark, #3A1A00)',
      background: active ? 'rgba(230,81,0,0.10)' : 'transparent',
    };
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('common.changeLanguage')}
        translate="no"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          minHeight: 44, minWidth: 62, padding: '0 12px',
          borderRadius: 22,
          fontFamily: 'var(--font-body, sans-serif)', fontSize: 13, fontWeight: 700,
          letterSpacing: 0.4, lineHeight: 1,
          cursor: 'pointer',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          ...btnStyle,
        }}
      >
        <IconGlobe />
        <span translate="no">{LANG_LABELS[lang]}</span>
        <IconCaret open={open} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('common.changeLanguage')}
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 168, margin: 0, padding: 6,
            listStyle: 'none', borderRadius: 14, zIndex: 60,
            ...menuStyle,
          }}
        >
          {LANGS.map((code) => {
            const active = code === lang;
            return (
              <li
                key={code}
                role="option"
                aria-selected={active}
                tabIndex={0}
                onClick={() => choose(code)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(code); }
                }}
                translate="no"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  minHeight: 44, padding: '0 14px', borderRadius: 10,
                  fontFamily: 'var(--font-body, sans-serif)', fontSize: 15,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  ...rowStyle(active),
                }}
              >
                <span>{LANG_NAMES[code]}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ opacity: 0.6, fontSize: 12, fontWeight: 600 }}>{LANG_LABELS[code]}</span>
                  <span style={{ width: 15, display: 'inline-flex' }}>{active ? <IconCheck /> : null}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
